import ArticleLayout from './components/ArticleLayout';
import { articlesMeta } from '@/mocks/articlesData';

const data = articlesMeta.find((a) => a.slug === 'how-to-use-sugargoo-spreadsheet')!;

export default function HowToUse() {
  return (
    <ArticleLayout data={data}>
      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Introduction: Unlocking the Full Potential
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          A <strong>Sugargoo Spreadsheet</strong> is more than a simple list of products. In the hands of a skilled user, it becomes a powerful shopping research tool that can save hours of browsing, prevent costly mistakes, and help you build a curated wardrobe or collection at a fraction of retail prices.
        </p>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          This guide goes beyond the basics. We cover advanced filtering techniques, wishlist strategies, quality assessment shortcuts, and workflow optimization tips that experienced spreadsheet users have developed over thousands of orders. Whether you are new to spreadsheets or looking to level up your approach, these techniques will transform how you shop.
        </p>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
          Let us turn product discovery from a time-consuming chore into an efficient, enjoyable process.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Understanding Spreadsheet Structure and Fields
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          Most Sugargoo Spreadsheets share a common structure. Understanding what each field means helps you read entries faster and make better decisions:
        </p>
        <div className="space-y-3 mb-4">
          {[
            { field: 'Product Name', meaning: 'The item description as listed by the seller. May include brand references, style notes, or sizing hints.' },
            { field: 'Category', meaning: 'Organizational grouping (Shoes, Hoodies, Accessories, etc.). Use this to filter and browse efficiently.' },
            { field: 'Price', meaning: 'Reference price in the original marketplace currency. Often marked as budget, mid-range, or premium for quick assessment.' },
            { field: 'Quality Notes', meaning: 'Community feedback on material, accuracy, sizing, and value. This is the most valuable field for purchase decisions.' },
            { field: 'Product Link', meaning: 'Direct URL to the marketplace listing. Click to verify current availability, price, and seller rating.' },
            { field: 'Image', meaning: 'Visual reference showing the actual or representative product appearance. Compare with listing photos for consistency.' },
            { field: 'Date Added', meaning: 'When the entry was added to the spreadsheet. Fresher entries are more likely to have active links and current prices.' },
          ].map((item) => (
            <div key={item.field} className="flex gap-3 p-3 bg-background-100/60 rounded-xl border border-background-200/60">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 shrink-0 mt-0.5">
                <i className="ri-table-line w-4 h-4 flex items-center justify-center"></i>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-800 mb-0.5">{item.field}</h4>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{item.meaning}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
          Not all spreadsheets use identical field names, but the concepts remain the same. Spend a few minutes understanding the layout of any new spreadsheet before diving into product browsing.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Advanced Browsing and Filtering Techniques
        </h2>
        <div className="space-y-4">
          {[
            {
              title: 'Filter by Category First',
              desc: 'Instead of scrolling through hundreds of random items, use category filters to focus on one product type at a time. This keeps your decision-making sharp and prevents option fatigue.',
            },
            {
              title: 'Sort by Price Range',
              desc: 'If you have a budget in mind, sort or filter by price tier. This immediately eliminates options outside your range and lets you focus on realistic choices.',
            },
            {
              title: 'Check Quality Notes Before Images',
              desc: 'Experienced users read quality notes before looking at product photos. If the notes flag sizing issues, material concerns, or accuracy problems, you can skip the item regardless of how good the photo looks.',
            },
            {
              title: 'Cross-Reference with Fresh Listings',
              desc: 'Always click the product link to verify the item is still available and the price has not changed significantly. Spreadsheet entries can become outdated, and marketplace listings frequently update.',
            },
            {
              title: 'Use Multiple Spreadsheets',
              desc: 'Different curators focus on different product types, price ranges, and quality levels. Following 2-3 quality spreadsheets gives you broader coverage and multiple perspectives on the same products.',
            },
            {
              title: 'Note Seller Patterns',
              desc: 'As you browse, you will start noticing which sellers appear repeatedly with positive notes. These become your trusted sources. Keep a personal list of reliable sellers for future reference.',
            },
          ].map((technique) => (
            <div key={technique.title} className="flex gap-3 p-4 bg-background-100/60 rounded-xl border border-background-200/60">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 shrink-0 mt-0.5">
                <i className="ri-filter-3-line w-4 h-4 flex items-center justify-center"></i>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-800 mb-1">{technique.title}</h4>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{technique.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Building and Managing Your Wishlist
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          A wishlist is your personal shortlist of interesting items. Proper wishlist management transforms scattered browsing into strategic shopping:
        </p>
        <div className="space-y-4">
          {[
            {
              step: 'Create Categories',
              desc: 'Organize your wishlist by product type (Shoes, Tops, Bottoms, Accessories) or by priority (Must-Have, Maybe, Research Later). This prevents a disorganized mess that becomes overwhelming.',
            },
            {
              step: 'Add Context Notes',
              desc: 'For each wishlist item, note why you are interested, what size you need, and any concerns from the spreadsheet quality notes. This context prevents forgetting important details.',
            },
            {
              step: 'Set a Budget Ceiling',
              desc: 'Before you start wishlisting, decide your total budget. When your wishlist exceeds your budget, force-rank items and cut the bottom entries. This prevents overspending.',
            },
            {
              step: 'Plan Shipping Groups',
              desc: 'As you build your wishlist, mentally group items by shipping urgency. Time-sensitive items go in one planned shipment; non-urgent items in another with economy shipping.',
            },
            {
              step: 'Review Before Ordering',
              desc: 'Never order directly from your wishlist without a final review. Re-read quality notes, check current prices, and verify links are still active. Spreadsheet entries age quickly.',
            },
          ].map((item, i) => (
            <div key={item.step} className="flex gap-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-sm font-semibold shrink-0">
                {i + 1}
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground-800 mb-1">{item.step}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Quality Assessment Shortcuts
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          Reading quality notes efficiently separates experienced shoppers from beginners. Here is how to assess quality quickly:
        </p>
        <div className="space-y-3">
          {[
            {
              label: 'Green Flags',
              indicators: [
                'Multiple positive reviews from different buyers',
                'Notes mention accurate sizing and good material',
                'Photos match listing description closely',
                'Seller has high rating and long history',
                'Price aligns with quality level (not suspiciously cheap)',
              ],
            },
            {
              label: 'Red Flags',
              indicators: [
                'Sizing consistently runs very small or very large',
                'Material feels cheap or different from description',
                'Logo placement is noticeably off',
                'Multiple buyers report the same defect',
                'Price is dramatically lower than comparable items',
              ],
            },
          ].map((group) => (
            <div key={group.label} className="bg-background-100/60 rounded-xl p-4 md:p-5 border border-background-200/60">
              <h4 className="text-sm font-semibold text-foreground-800 mb-2">{group.label}</h4>
              <ul className="space-y-1.5">
                {group.indicators.map((ind, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <i className={`${group.label === 'Green Flags' ? 'ri-check-line text-accent-500' : 'ri-close-line text-secondary-500'} w-3 h-3 flex items-center justify-center mt-0.5 shrink-0`}></i>
                    <span className="text-xs text-foreground-700">{ind}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Converting Spreadsheet Finds to Orders
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          The ultimate goal of spreadsheet browsing is turning discoveries into successful orders. Here is the optimized workflow:
        </p>
        <div className="space-y-4">
          {[
            {
              step: 'Verify the Link',
              desc: 'Click the product link to confirm the item is still available and the price matches the spreadsheet entry. Check the seller current rating and recent reviews.',
            },
            {
              step: 'Confirm Sizing',
              desc: 'Review the seller size chart carefully. Compare measurements to items you already own. When in doubt, size up — it is easier to tailor down than to stretch up.',
            },
            {
              step: 'Copy and Submit',
              desc: 'Copy the product URL into Sugargoo order form. Add notes referencing the spreadsheet quality feedback and any specific instructions.',
            },
            {
              step: 'Track and Wait',
              desc: 'Monitor the order status through your Sugargoo dashboard. Domestic shipping to the warehouse usually takes 3-7 days depending on the seller location.',
            },
            {
              step: 'QC Review Decision',
              desc: 'When QC photos arrive, compare them against the spreadsheet notes and your expectations. Approve, return, or adjust your plan accordingly.',
            },
          ].map((item, i) => (
            <div key={item.step} className="flex gap-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-sm font-semibold shrink-0">
                {i + 1}
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground-800 mb-1">{item.step}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Pro Tips from Experienced Spreadsheet Users
        </h2>
        <div className="space-y-3">
          {[
            'Check spreadsheet update dates — stale entries often have dead links or changed prices',
            'Follow spreadsheet curators on social media for real-time updates and flash finds',
            'Create a personal blacklist of sellers with consistent negative notes',
            'Screenshot items you are seriously considering — listings sometimes disappear',
            'Join community discussions about specific items before committing to large orders',
            'Keep a spreadsheet of your own purchases with sizing results for future reference',
            'Share your own quality feedback to help the community — good spreadsheets thrive on contributions',
          ].map((tip, i) => (
            <div key={i} className="flex gap-3">
              <div className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 shrink-0 mt-0.5">
                <i className="ri-lightbulb-line w-3 h-3 flex items-center justify-center"></i>
              </div>
              <span className="text-sm text-foreground-700">{tip}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Conclusion: Master the Spreadsheet, Master Your Shopping
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          A <strong>Sugargoo Spreadsheet</strong> is only as useful as the user browsing it. The techniques in this guide — structured filtering, strategic wishlist management, efficient quality assessment, and streamlined order conversion — separate efficient shoppers from those who waste hours browsing and still make disappointing purchases.
        </p>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          The most successful international shoppers treat product discovery as a skill. They use spreadsheets as research tools, not just link collections. They cross-reference community feedback, verify current listings, plan shipments strategically, and learn from every order to improve the next one.
        </p>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
          Start applying these techniques today. Your future self — with a wardrobe full of quality finds at unbeatable prices — will thank you.
        </p>
      </section>
    </ArticleLayout>
  );
}